import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminListings } from '../api/client';
import type { Listing } from '../api/client';
import BackToDashboard from '../components/BackToDashboard';
import { formatPrice } from '../utils/format';

const PAGE_SIZE = 50;
type SortOption = 'newest' | 'oldest' | 'price_asc' | 'price_desc' | 'title';

export default function AdminAllListings() {
  const [items, setItems] = useState<Listing[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [pageStack, setPageStack] = useState<Array<{ token: string; items: Listing[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');

  const load = (token?: string) => {
    setLoading(true);
    setError(null);
    adminListings
      .listAll({ limit: String(PAGE_SIZE), nextToken: token })
      .then((res) => {
        setItems(res.items);
        setNextToken(res.nextToken);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const goToNextPage = () => {
    if (!nextToken || loading) return;
    setPageStack((prev) => [...prev, { token: nextToken, items: [...items] }]);
    load(nextToken);
  };

  const goToPreviousPage = () => {
    if (pageStack.length === 0 || loading) return;
    const stack = [...pageStack];
    const prev = stack.pop();
    if (!prev) return;
    setPageStack(stack);
    setItems(prev.items);
    setNextToken(prev.token);
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = async (l: Listing) => {
    setActingId(l.listingId);
    try {
      await adminListings.approve(l.listingId, l.orgId);
      setItems((prev) => prev.map((i) => (i.listingId === l.listingId ? { ...i, status: 'published' } : i)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActingId(null);
    }
  };

  const handleUnpublish = async (l: Listing) => {
    setActingId(l.listingId);
    try {
      await adminListings.unpublish(l.listingId, l.orgId);
      setItems((prev) => prev.map((i) => (i.listingId === l.listingId ? { ...i, status: 'unpublished' } : i)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActingId(null);
    }
  };

  const handleExtendExpiry = async (l: Listing) => {
    setActingId(l.listingId);
    try {
      const updated = await adminListings.extendExpiry(l.listingId, l.orgId, {});
      setItems((prev) => prev.map((i) => (i.listingId === l.listingId ? { ...i, expiresAt: updated.expiresAt } : i)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActingId(null);
    }
  };

  const statusClass = (s: string) => {
    if (s === 'published') return 'dash-badge dash-badge--success';
    if (s === 'pending_review') return 'dash-badge dash-badge--warning';
    if (s === 'unpublished') return 'dash-badge dash-badge--danger';
    return 'dash-badge dash-badge--neutral';
  };

  const filteredAndSorted = useMemo(() => {
    let list = [...items];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((l) => (l.titleEn ?? '').toLowerCase().includes(q) || (l.orgId ?? '').toLowerCase().includes(q));
    if (filterStatus) list = list.filter((l) => l.status === filterStatus);
    if (filterType) list = list.filter((l) => l.type === filterType);
    if (sortBy === 'newest') list.sort((a, b) => (new Date(b.updatedAt ?? b.createdAt).getTime()) - (new Date(a.updatedAt ?? a.createdAt).getTime()));
    else if (sortBy === 'oldest') list.sort((a, b) => (new Date(a.updatedAt ?? a.createdAt).getTime()) - (new Date(b.updatedAt ?? b.createdAt).getTime()));
    else if (sortBy === 'price_asc') list.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    else if (sortBy === 'price_desc') list.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    else if (sortBy === 'title') list.sort((a, b) => (a.titleEn ?? '').localeCompare(b.titleEn ?? ''));
    return list;
  }, [items, search, filterStatus, filterType, sortBy]);

  if (loading && items.length === 0) {
    return (
      <div className="dash-page">
        <BackToDashboard />
        <p style={{ color: '#6b7280' }}>Loading all ads…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-page">
        <BackToDashboard />
        <div className="dash-alert dash-alert--error">{error}</div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <BackToDashboard />
      <header className="dash-page-header">
        <h1 className="dash-page-title">All ads</h1>
        <p className="dash-page-desc">
          Approve pending ads to publish them; unpublish to take them off the public site.
        </p>
      </header>

      {items.length === 0 ? (
        <section className="dash-card">
          <div className="dash-card-body">
            <div className="dash-empty">No listings yet.</div>
          </div>
        </section>
      ) : (
        <section className="dash-card">
          <div className="dash-card-body">
            <div className="dash-toolbar">
              <input
                type="search"
                placeholder="Search by title or org…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dash-form-input"
                style={{ minWidth: 200 }}
              />
              <span className="dash-toolbar-label">Status</span>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="dash-form-select">
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="pending_review">Pending review</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
              </select>
              <span className="dash-toolbar-label">Type</span>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="dash-form-select">
                <option value="">All</option>
                <option value="rent">Rent</option>
                <option value="sale">Sale</option>
                <option value="short_term">Short term</option>
              </select>
              <span className="dash-toolbar-label">Sort</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)} className="dash-form-select">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="price_asc">Price: low to high</option>
                <option value="price_desc">Price: high to low</option>
                <option value="title">Title</option>
              </select>
            </div>
            <p className="dash-pagination-info" style={{ marginBottom: '0.75rem', textAlign: 'left' }}>
              Showing {filteredAndSorted.length}{items.length !== filteredAndSorted.length ? ` of ${items.length}` : ''} listings
            </p>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Org</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.map((l) => (
                    <tr key={l.listingId}>
                      <td style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{l.orgId}</td>
                      <td style={{ fontWeight: 500 }}>{l.titleEn}</td>
                      <td style={{ textTransform: 'capitalize' }}>{l.type}</td>
                      <td><span className={statusClass(l.status)}>{l.status === 'pending_review' ? 'Pending review' : l.status}</span></td>
                      <td style={{ textAlign: 'right' }}>{formatPrice(l.price, l.currency)}</td>
                      <td>
                        <div className="dash-actions-inline">
                          {l.status === 'pending_review' && (
                            <button type="button" disabled={actingId === l.listingId} onClick={() => handleApprove(l)} className="dash-btn dash-btn--primary dash-btn--sm">
                              {actingId === l.listingId ? '…' : 'Approve'}
                            </button>
                          )}
                          {l.status === 'published' && (
                            <>
                              <button type="button" disabled={actingId === l.listingId} onClick={() => handleExtendExpiry(l)} className="dash-btn dash-btn--secondary dash-btn--sm" title="Extend expiry by 1 month">
                                {actingId === l.listingId ? '…' : 'Extend expiry'}
                              </button>
                              <button type="button" disabled={actingId === l.listingId} onClick={() => handleUnpublish(l)} className="dash-btn dash-btn--danger dash-btn--sm">
                                {actingId === l.listingId ? '…' : 'Unpublish'}
                              </button>
                            </>
                          )}
                          <Link to={`/dashboard/listings/edit/${l.listingId}?orgId=${encodeURIComponent(l.orgId)}`} style={{ color: '#2563eb', fontWeight: 500, fontSize: '0.875rem' }}>Edit</Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(nextToken || pageStack.length > 0) && (
              <nav role="navigation" aria-label="Listings pagination" className="dash-pagination">
                <button type="button" onClick={goToPreviousPage} disabled={loading || pageStack.length === 0} className="dash-btn dash-btn--secondary dash-btn--sm">← Previous page</button>
                <span className="dash-pagination-info">Page {pageStack.length + 1}</span>
                <button type="button" onClick={goToNextPage} disabled={loading || !nextToken} className="dash-btn dash-btn--secondary dash-btn--sm">{loading ? 'Loading…' : 'Next page →'}</button>
              </nav>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
